import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography,
} from "@mui/material";
import { actualizarEgreso, buscarClientesEgresos, getCatalogosEgresos } from "../services/egresosService";
import { fieldSx } from "./formStyles";
import { TituloSeccion } from "./ComprobanteUi";

const MES_RE = /^(.+?) del (\d{4})$/;

// Edición del comprobante (modificar_comprobante.php). Si se deja vacío el importe se conservan los montos actuales.
// Solo se elige de la lista; si el valor ya cargado no está en ella, se mantiene visible para no perderlo
const opcionesNombre = (nombres = [], actual) => (actual && !nombres.includes(actual) ? [actual, ...nombres] : nombres);

export default function ComprobanteEditarDialog({ abierto, data, onClose, onGuardado }) {
  const [cat, setCat] = useState(null);
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [clienteModo, setClienteModo] = useState("mantener"); // mantener | ninguno | cambiar
  const [cliente, setCliente] = useState(null);
  const [opcionesCliente, setOpcionesCliente] = useState([]);

  const e = data?.egreso;

  useEffect(() => {
    if (!abierto) return;
    setError(""); setClienteModo("mantener"); setCliente(null);
    getCatalogosEgresos().then((r) => setCat(r.data)).catch(() => setError("No se pudieron cargar los datos del formulario."));
  }, [abierto]);

  useEffect(() => {
    if (!abierto || !e || !cat) return;
    const m = MES_RE.exec(String(e.mes || ""));
    setF({
      fecha: String(e.fecha).slice(0, 10), cambio: String(e.cambio ?? ""), dirigido: e.nombre || "", deposito: e.deposito || "", num_cheque: e.nocheque || "",
      ciudad: e.ciudad || "", banco: e.banco || "", servicio: e.servicio || "", num_contrato: e.nro_contrato || "", num_referencia: e.nro_referencia || "",
      importe: "", moneda: e.moneda === "Dolares" ? "Dolares" : "Bolivianos", concepto: cat.conceptos.find((c) => c.concepto === e.concepto) || null,
      mesNombre: m ? m[1] : cat.meses[new Date().getMonth()], year: m ? Number(m[2]) : new Date().getFullYear(), observacion: e.obs || "", retencion: e.tipo_retencion || "",
    });
  }, [abierto, e, cat]);

  const set = (k) => (ev) => setF((p) => ({ ...p, [k]: ev.target.value }));
  const buscarCliente = (texto) => {
    if (texto.trim().length < 2) { setOpcionesCliente([]); return; }
    buscarClientesEgresos(texto.trim()).then((r) => setOpcionesCliente(r.data || [])).catch(() => setOpcionesCliente([]));
  };

  const years = useMemo(() => (f ? [-2, -1, 0, 1].map((d) => new Date().getFullYear() + d).concat(f.year).filter((v, i, a) => a.indexOf(v) === i).sort() : []), [f]);

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      const body = {
        fecha: f.fecha, cambio: f.cambio, dirigido: f.dirigido, deposito: f.deposito, num_cheque: f.num_cheque, ciudad: f.ciudad, banco: f.banco,
        servicio: f.servicio, num_contrato: f.num_contrato, num_referencia: f.num_referencia, importe: f.importe, moneda: f.moneda,
        conceptoId: f.concepto?.id, mes: `${f.mesNombre} del ${f.year}`, observacion: f.observacion, retencion: f.retencion,
      };
      if (clienteModo === "ninguno") body.codServicioCliente = null;
      if (clienteModo === "cambiar") {
        if (!cliente) { setError("Selecciona el cliente o elige otra opción."); setGuardando(false); return; }
        body.codServicioCliente = cliente.id;
      }
      await actualizarEgreso(e.id, body);
      onGuardado("Comprobante actualizado.");
      onClose();
    } catch (er) {
      setError(er?.response?.data?.error || "No se pudo guardar el comprobante.");
    } finally {
      setGuardando(false);
    }
  };

  const campo = (k, label, size = { xs: 12, sm: 6, md: 3 }, extra = {}) => (
    <Grid size={size}><TextField fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx} {...extra} /></Grid>
  );
  const select = (k, label, opciones, size = { xs: 12, sm: 6, md: 3 }) => (
    <Grid size={size}>
      <TextField select fullWidth size="small" label={label} value={f[k]} onChange={set(k)} sx={fieldSx}>
        {opciones.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    </Grid>
  );

  return (
    <Dialog open={abierto} onClose={() => !guardando && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>Editar comprobante N° {e?.id}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
        {!f && !error && <CircularProgress size={20} />}
        {f && cat && (
          <Grid container spacing={0.75}>
            <TituloSeccion>Datos del comprobante</TituloSeccion>
            {campo("fecha", "Fecha de registro", undefined, { type: "date", slotProps: { inputLabel: { shrink: true } } })}
            {campo("cambio", "Tipo de cambio")}
            {campo("num_cheque", "N° de cheque")}
            {select("ciudad", "Ciudad destino", cat.ciudades)}

            <TituloSeccion>Beneficiario</TituloSeccion>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete size="small" options={opcionesNombre(cat.nombres, f.dirigido)} value={f.dirigido || null} onChange={(_, v) => setF((p) => ({ ...p, dirigido: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
                renderInput={(p) => <TextField {...p} label="Dirigido a" sx={fieldSx} />} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete size="small" options={opcionesNombre(cat.nombres, f.deposito)} value={f.deposito || null} onChange={(_, v) => setF((p) => ({ ...p, deposito: v || "" }))} noOptionsText="Sin resultados: el nombre debe estar en la lista"
                renderInput={(p) => <TextField {...p} label="Depositado a" sx={fieldSx} />} />
            </Grid>

            <TituloSeccion>Detalle del pago</TituloSeccion>
            {campo("num_contrato", "N° de contrato")}
            {campo("num_referencia", "N° de referencia")}
            {select("servicio", "Tipo de servicio", cat.servicios.includes(f.servicio) ? cat.servicios : [f.servicio, ...cat.servicios])}
            {select("banco", "Banco", cat.bancos.includes(f.banco) ? cat.bancos : [f.banco, ...cat.bancos])}

            <TituloSeccion>Importe y concepto</TituloSeccion>
            {campo("importe", "Importe (vacío = conservar)", { xs: 6, md: 2 })}
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField select fullWidth size="small" label="Moneda" value={f.moneda} onChange={set("moneda")} sx={fieldSx}>
                <MenuItem value="Bolivianos">Bolivianos</MenuItem>
                <MenuItem value="Dolares">Dólares</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete size="small" options={cat.conceptos} value={f.concepto} onChange={(_, v) => setF((p) => ({ ...p, concepto: v }))}
                getOptionLabel={(o) => o?.concepto || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(p) => <TextField {...p} label="Concepto" sx={fieldSx} />} />
            </Grid>
            {select("mesNombre", "Mes de servicio", cat.meses, { xs: 6, md: 2.5 })}
            <Grid size={{ xs: 6, md: 1.5 }}>
              <TextField select fullWidth size="small" label="Año" value={f.year} onChange={set("year")} sx={fieldSx}>
                {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>

            <Grid size={12}><TextField fullWidth multiline minRows={2} maxRows={6} size="small" label="Observación" value={f.observacion} onChange={set("observacion")} sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], py: "3px" } }} /></Grid>

            <TituloSeccion>Retención y cliente</TituloSeccion>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField select fullWidth size="small" label="Retención" value={f.retencion} onChange={set("retencion")} sx={fieldSx}>
                <MenuItem value="">Sin retención</MenuItem>
                {cat.retenciones.map((r) => <MenuItem key={r.id} value={r.nombre}>{r.nombre} ({r.calculo}%)</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField select fullWidth size="small" label="Cliente" value={clienteModo} onChange={(ev) => setClienteModo(ev.target.value)} sx={fieldSx}>
                <MenuItem value="mantener">Mantener: {data?.cliente || "sin cliente"}</MenuItem>
                <MenuItem value="ninguno">Sin cliente</MenuItem>
                <MenuItem value="cambiar">Cambiar cliente…</MenuItem>
              </TextField>
            </Grid>
            {clienteModo === "cambiar" && (
              <Grid size={{ xs: 12, md: 5 }}>
                <Autocomplete size="small" options={opcionesCliente} value={cliente} filterOptions={(x) => x}
                  getOptionLabel={(o) => `${o.razon_social} ${o.servicio}${o.descripcion ? ` - ${o.descripcion}` : ""}`} isOptionEqualToValue={(a, b) => a.id === b.id}
                  onInputChange={(_, v, reason) => { if (reason === "input") buscarCliente(v); }} onChange={(_, v) => setCliente(v)}
                  noOptionsText="Escribe para buscar un cliente" renderInput={(p) => <TextField {...p} label="Buscar cliente" sx={fieldSx} />} />
              </Grid>
            )}
          </Grid>
        )}
        <Typography sx={{ fontSize: 11, mt: 1 }} color="text.secondary">
          Si cambias el importe, la moneda o el tipo de cambio sin escribir el importe, se conservan los montos actuales en Bs y $us.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}>
        <Button size="small" onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button size="small" variant="contained" disabled={!f || guardando} onClick={guardar}>{guardando ? <CircularProgress size={16} color="inherit" /> : "Guardar"}</Button>
      </DialogActions>
    </Dialog>
  );
}
